import { useState } from 'react'
import { useSelector } from 'react-redux'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { Alert, Card, Pagination } from 'antd'
import { SALES_ORDER_DETAILS } from '@Orders/constants'
import {
    SALES_ORDER_LINES_PAGINATION_LIMIT,
    MOLDS_PAGINATION_LIMIT,
    DEFAULT_PAGE_NUMBER,
    SALES_LINES_PAGINATION_LIMIT,
} from '@Orders/logic'
import { getOffsetOfFirstAndLastRecord } from '@Orders/OrderDetailsPage/Status/Shipment/components/logic'
import { selectFlags } from '@Orders/OrderDetailsPage/selectors'
import { selectFlags as selectMaafcoFlags } from '@Quotes/selectors'
import { PROVISIONED_UI_FLAGS } from '@Orders/OrderDetailsPage/logic'

import Box from '~/core/Box'
import Button from '~/core/Button'
import Text from '~/core/Text'
import { DEFAULT_REFRESH_MSG } from '~/lib/flashMsgs'
import { BORDER } from '~/apps/consumer/pages/Quotes/Admin/AdminUserProfilePage/constants'
import { useFlagEnabled } from '~/lib/isFlagEnabled'

import { useOrderedItemsDataQuery } from './logic'
import SalesOrderLinesTable from './SalesOrderLinesTable'
import { OrderedItemsDataTableType } from './types'

export default function OrderedItemsDataTable(
    props: OrderedItemsDataTableType,
) {
    const {
        cursor,
        isImOrder,
        isImReorder,
        onClickPrevious,
        onClickNext,
        orderId,
        pageNumber,
        setSelectedTab,
        setSelectedShipmentId,
        shippedBy,
        shipmentIdsMap,
        shipments,
    } = props

    const [currentPageNumber, setPageNumber] = useState(DEFAULT_PAGE_NUMBER)

    const upcomingPageOffset = MOLDS_PAGINATION_LIMIT * (currentPageNumber - 1)

    const { isImToolingEnabled } = useSelector(selectFlags)
    const { isImMaafcoEnabled } = useSelector(selectMaafcoFlags)
    const isNonImDfmEnabled = useFlagEnabled(PROVISIONED_UI_FLAGS.NON_IM_DFM_UI)

    const showNumberedPagination = isImOrder && isImToolingEnabled

    const { data, loading, error, refetch } = useOrderedItemsDataQuery(
        orderId,
        [],
        showNumberedPagination
            ? MOLDS_PAGINATION_LIMIT
            : SALES_ORDER_LINES_PAGINATION_LIMIT,
        upcomingPageOffset,
        isImMaafcoEnabled,
        cursor,
    )

    if (loading) {
        return (
            <Card loading bordered={false}>
                content
            </Card>
        )
    }

    if (error) {
        return <Alert message={DEFAULT_REFRESH_MSG} type="error" showIcon />
    }

    const onChangeActivity = (upcomingPageNumber: number) => {
        const salesOrderList = refetch({
            limit: SALES_LINES_PAGINATION_LIMIT,
            offset: upcomingPageOffset,
        })

        setPageNumber(upcomingPageNumber)

        return salesOrderList
    }

    const salesOrderLines = data?.salesOrderLines

    const isHighTouch =
        isNonImDfmEnabled &&
        !isImOrder &&
        data?.salesOrderLines.items?.every(item => {
            return item?.quoteModelRevision?.quoteModel?.quote?.isCpqOwned
        })

    const totalOrderedItems = salesOrderLines?.totalCount

    const numberOfRecordsOnPage = salesOrderLines?.items?.length

    const { offsetOfFirstRecordOfPage, offsetOfLastRecordOfPage } =
        getOffsetOfFirstAndLastRecord(
            showNumberedPagination ? currentPageNumber : pageNumber,
            numberOfRecordsOnPage,
            showNumberedPagination
                ? MOLDS_PAGINATION_LIMIT
                : SALES_ORDER_LINES_PAGINATION_LIMIT,
        )

    const cursorBasedPagination = (onPaginationChange: () => void) => {
        const previousCursor = salesOrderLines?.cursor?.previous
        const nextCursor = salesOrderLines?.cursor?.next

        if (
            !totalOrderedItems ||
            totalOrderedItems <= SALES_ORDER_LINES_PAGINATION_LIMIT
        ) {
            return null
        }

        return (
            <Box justifyContent="end" marginTop="20px">
                <Button.Group>
                    <Text style={{ marginRight: '7px' }}>
                        {offsetOfFirstRecordOfPage}-{offsetOfLastRecordOfPage}{' '}
                        of {totalOrderedItems}
                    </Text>
                    <Button
                        size="small"
                        placement={SALES_ORDER_DETAILS}
                        name="left"
                        disabled={!previousCursor}
                        onClick={() => {
                            onClickPrevious(previousCursor)
                            onPaginationChange()
                        }}
                        data-test-target="ordered-items-prev-btn"
                    >
                        <LeftOutlined />
                    </Button>
                    <Button
                        size="small"
                        placement={SALES_ORDER_DETAILS}
                        name="right"
                        disabled={!nextCursor}
                        onClick={() => {
                            onClickNext(nextCursor)
                            onPaginationChange()
                        }}
                        data-test-target="ordered-items-next-btn"
                    >
                        <RightOutlined />
                    </Button>
                </Button.Group>
            </Box>
        )
    }

    const numberBasedPagination = (onPaginationChange: () => void) => {
        if (!totalOrderedItems || totalOrderedItems <= MOLDS_PAGINATION_LIMIT) {
            return null
        }
        return (
            <Pagination
                style={{ margin: '10px' }}
                pageSize={MOLDS_PAGINATION_LIMIT}
                current={currentPageNumber || DEFAULT_PAGE_NUMBER}
                defaultPageSize={MOLDS_PAGINATION_LIMIT}
                onChange={number => {
                    onChangeActivity(number)
                    onPaginationChange()
                }}
                total={totalOrderedItems}
                showSizeChanger={false}
                showTotal={(total: number) =>
                    `${offsetOfFirstRecordOfPage}-${offsetOfLastRecordOfPage} of ${total}`
                }
            />
        )
    }

    const refetchVariables = {
        limit: showNumberedPagination
            ? MOLDS_PAGINATION_LIMIT
            : SALES_ORDER_LINES_PAGINATION_LIMIT,
        offset: upcomingPageOffset,
    }

    return (
        <Card
            style={{
                borderRadius: '4px',
                border: BORDER,
            }}
        >
            {totalOrderedItems ? (
                <SalesOrderLinesTable
                    cursorBasedPagination={cursorBasedPagination}
                    isImOrder={isImOrder}
                    isImReorder={isImReorder}
                    numberBasedPagination={numberBasedPagination}
                    orderId={orderId}
                    refetchVariables={refetchVariables}
                    shipmentDetail={shipments}
                    salesOrderLines={salesOrderLines}
                    setSelectedTab={setSelectedTab}
                    setSelectedShipmentId={setSelectedShipmentId}
                    shippedBy={shippedBy}
                    shipmentIdsMap={shipmentIdsMap}
                    shipments={shipments}
                    isHighTouch={isHighTouch || false}
                />
            ) : (
                <Box alignItems="start" height="100%" width="100%">
                    No parts remaining to be shipped.
                </Box>
            )}
        </Card>
    )
}
